/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { createApi } from "@reduxjs/toolkit/query/react"

import { BULK_UPLOAD_TABLE_RECORD_LIMIT } from "constants/menu_bulk_upload"
import {
  BulkUploadTask,
  BulkUploadTaskStatus,
  CompleteBulkTaskRequest,
  CreateBulkTaskRequest,
  CreateBulkTaskResponse,
  GetBulkUploadTaskRequest,
  GetBulkUploadTaskResponse,
} from "types/delv-platform-api/bulkUploadTask"

import { axiosBaseQuery, createRequestHeaders } from "./apiClient"
import {
  apiReducerPaths,
  bulkUploadEndpoints,
  dpApiMerchantUrl,
  CACHE_QUERY_TAGS,
} from "./constants"
import { MerchantContext, HttpMethod } from "./types"

interface GetBulkTasksParams
  extends GetBulkUploadTaskRequest,
    MerchantContext {}

interface CreateBulkTaskParams extends CreateBulkTaskRequest, MerchantContext {}

interface CompleteBulkTaskParams
  extends CompleteBulkTaskRequest,
    MerchantContext {}

export const bulkUploadApi = createApi({
  reducerPath: apiReducerPaths.bulkUploadApi,
  baseQuery: axiosBaseQuery(dpApiMerchantUrl),
  tagTypes: [CACHE_QUERY_TAGS.bulkTasks],
  endpoints: builder => ({
    getBulkTasks: builder.query<GetBulkUploadTaskResponse, GetBulkTasksParams>({
      query: ({ merchantID, merchantGroupID, ...params }) => ({
        url: bulkUploadEndpoints.getBulkTasks,
        headers: createRequestHeaders({ merchantID, merchantGroupID }),
        params,
      }),
      providesTags: [CACHE_QUERY_TAGS.bulkTasks],
    }),
    createBulkTask: builder.mutation<
      CreateBulkTaskResponse,
      CreateBulkTaskParams
    >({
      query: ({ merchantID, merchantGroupID, ...data }) => ({
        url: bulkUploadEndpoints.createBulkTask,
        method: HttpMethod.POST,
        headers: createRequestHeaders({ merchantID, merchantGroupID }),
        data,
      }),
    }),
    completeBulkTask: builder.mutation<void, CompleteBulkTaskParams>({
      query: ({ merchantID, merchantGroupID, taskID, ...data }) => ({
        url: bulkUploadEndpoints.completeBulkTask(taskID),
        method: HttpMethod.PUT,
        headers: createRequestHeaders({ merchantID, merchantGroupID }),
        data: { taskID, ...data },
      }),
      onQueryStarted: async (
        { merchantID, merchantGroupID, taskID, uploadType },
        { dispatch, queryFulfilled },
      ) => {
        try {
          await queryFulfilled

          // Push a temp TaskNew entry so the processing banner shows immediately
          dispatch(
            bulkUploadApi.util.updateQueryData(
              "getBulkTasks",
              {
                merchantID,
                merchantGroupID,
                uploadType,
                limit: BULK_UPLOAD_TABLE_RECORD_LIMIT,
              },
              draft => {
                const existingTask = draft.tasks.find(t => t.taskID === taskID)
                if (!existingTask) {
                  draft.tasks.unshift({
                    taskID,
                    status: BulkUploadTaskStatus.TaskNew,
                  } as BulkUploadTask)
                }
              },
            ),
          )
          // Invalidate cache to refetch and sync with actual data after showing processing banner
          dispatch(
            bulkUploadApi.util.invalidateTags([CACHE_QUERY_TAGS.bulkTasks]),
          )
        } catch {
          // Mutation failed — no temp task injected, no refetch triggered
        }
      },
    }),
  }),
})

export const {
  useGetBulkTasksQuery,
  useCreateBulkTaskMutation,
  useCompleteBulkTaskMutation,
} = bulkUploadApi
